import type { EffectDefinition, EffectsProp } from 'remotion';

/** Runs public WebGL effect definitions with two reusable surfaces, like the
 * Remotion video renderer. No decoder, React updates or readPixels per frame. */
export function createNativeEffectRenderer(output: HTMLCanvasElement) {
  const surfaces = new Map<string, HTMLCanvasElement[]>();
  const states = new Map<EffectDefinition<unknown>, Map<HTMLCanvasElement, unknown>>();
  return {
    draw(source: CanvasImageSource, effects: EffectsProp, width: number, height: number) {
      if (output.width !== width) output.width = width;
      if (output.height !== height) output.height = height;
      let current = source;
      effects.forEach(({ definition, params }, i) => {
        if (definition.backend === 'webgpu') throw new Error('unsupported_effect_backend');
        let pair = surfaces.get(definition.backend);
        if (!pair) { pair = [document.createElement('canvas'), document.createElement('canvas')]; surfaces.set(definition.backend, pair); }
        const target = pair[i % 2];
        if (target.width !== width) target.width = width;
        if (target.height !== height) target.height = height;
        let entries = states.get(definition);
        if (!entries) { entries = new Map(); states.set(definition, entries); }
        if (!entries.has(target)) entries.set(target, definition.setup(target));
        definition.apply({ source: current, target, state: entries.get(target), params, width, height, gpuDevice: null, flipSourceY: true });
        if (definition.backend === 'webgl2' && target.getContext('webgl2')?.isContextLost()) throw new Error('effect_context_lost');
        current = target;
      });
      const context = output.getContext('2d');
      if (!context) throw new Error('effect_canvas_unavailable');
      context.clearRect(0, 0, width, height);
      context.drawImage(current, 0, 0, width, height);
    },
    dispose() {
      for (const [definition, entries] of states) for (const state of entries.values()) definition.cleanup(state);
      states.clear();
      for (const [backend, pair] of surfaces) for (const canvas of pair) {
        if (backend === 'webgl2') canvas.getContext('webgl2')?.getExtension('WEBGL_lose_context')?.loseContext();
        canvas.width = canvas.height = 1;
      }
      surfaces.clear();
      output.width = output.height = 1;
    },
  };
}
